import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { customEvent } from "goi_common";
import { ExternalLinkIcon } from "@/components/DirectionsIcons";

interface DirectionsMapProps {
  latitude: number;
  longitude: number;
  companyName: string;
  address: string;
  /**
   * 이벤트 path. 기본값은 장례식장 directions 페이지 것이라 기존 데이터가 안 끊긴다.
   * 장지 상세는 "prlr_facility", 화장장 상세는 "prlr_cremat",
   * 무빈소 안내는 "prlr_mubinso" 을 넘긴다.
   * 네 path 모두 btn_naver_map, btn_kakao_map trigger 를 갖고 있어야 한다.
   */
  eventPath?:
    | "prlr_directions"
    | "prlr_facility"
    | "prlr_cremat"
    | "prlr_mubinso";
  /** 주소를 다른 섹션에서 이미 보여주는 경우 false (장지 상세는 기본 정보 표에 있다) */
  showAddress?: boolean;
  /** 섹션 이동 nav 에서 앵커로 쓸 id */
  sectionId?: string;
}

declare global {
  interface Window {
    naver: any;
  }
}

export const DirectionsMap = ({
  latitude,
  longitude,
  companyName,
  address,
  eventPath = "prlr_directions",
  showAddress = true,
  sectionId,
}: DirectionsMapProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (!window.naver?.maps) return;

    const map = new window.naver.maps.Map(mapRef.current, {
      center: new window.naver.maps.LatLng(latitude, longitude),
      zoom: 16,
    });

    const marker = new window.naver.maps.Marker({
      position: new window.naver.maps.LatLng(latitude, longitude),
      map,
    });

    const infowindow = new window.naver.maps.InfoWindow({
      content: `<div style="padding:6px 12px;font-size:13px;font-weight:600;white-space:nowrap;">${companyName}</div>`,
      borderWidth: 1,
      borderRadius: 5,
      pixelOffset: new window.naver.maps.Point(0, -5),
      anchorSize: 0,
    });
    infowindow.open(map, marker);

    return () => map.destroy();
  }, [isLoaded, latitude, longitude, companyName]);

  const naverMapUrl = `https://map.naver.com/p/search/${encodeURIComponent(
    companyName
  )}`;
  const kakaoMapUrl = `https://map.kakao.com/link/to/${encodeURIComponent(
    companyName
  )},${latitude},${longitude}`;

  return (
    <section
      className="dr-section"
      id={sectionId}
      aria-labelledby="dr-map-title"
    >
      <Script
        src={`https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${process.env.NEXT_PUBLIC_NAVERMAP_KEY}`}
        strategy="afterInteractive"
        onLoad={() => setIsLoaded(true)}
      />
      <h2 id="dr-map-title" className="dr-section__title">
        {`${companyName} 위치`}
      </h2>
      <p className="dr-section__lede">
        정확한 위치를 지도에서 확인하실 수 있습니다.
      </p>

      {showAddress && (
        <table
          className="dr-info-table dr-info-table--compact"
          aria-label={`${companyName} 주소`}
        >
          <tbody>
            <tr>
              <th scope="row">주소</th>
              <td>{address}</td>
            </tr>
          </tbody>
        </table>
      )}

      <div
        ref={mapRef}
        className="dr-map"
        role="img"
        aria-label={`${companyName} 지도`}
      />

      <div className="dr-map__actions">
        <a
          className="dr-btn dr-btn--naver"
          href={naverMapUrl}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() =>
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "btn_naver_map",
              },
            })
          }
        >
          <span className="dr-btn__multiline">
            <span>네이버 지도로</span>
            <span>
              길찾기
              <ExternalLinkIcon size={14} style={{ marginLeft: 4 }} />
            </span>
          </span>
        </a>
        <a
          className="dr-btn dr-btn--kakao"
          href={kakaoMapUrl}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() =>
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "btn_kakao_map",
              },
            })
          }
        >
          <span className="dr-btn__multiline">
            <span>카카오 지도로</span>
            <span>
              길찾기
              <ExternalLinkIcon size={14} style={{ marginLeft: 4 }} />
            </span>
          </span>
        </a>
      </div>
    </section>
  );
};
